import type * as React from "react";
import { Box, ButtonBase, IconButton, Modal, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useHistory } from "react-router-dom";
import PlanInformationGiftIcon from "assets/icons/plan-information-gift-icon.svg";
import PlanInformationModalCloseIcon from "assets/icons/plan-information-modal-close.svg";
import PlanInformationShareReferralIcon from "assets/icons/plan-information-share-referral-icon.svg";
import { PATHS } from "constants/pathConstants";
import { COLORS } from "components/v3/Theme/colors";
import { shareModalOptionCardSx } from "./planInformationStyles";

type ShareGenomicLifeOptionsModalProps = {
  open: boolean;
  onClose: () => void;
};

const ShareGenomicLifeOptionsModal: React.FC<
  ShareGenomicLifeOptionsModalProps
> = ({ open, onClose }) => {
  const history = useHistory();

  const handleGiftMembershipOption = () => {
    onClose();
    history.push(PATHS.ACCOUNT_MANAGEMENT_GIFT_MEMBERSHIP);
  };

  const handleTellSomeoneOption = () => {
    onClose();
    history.push(PATHS.ACCOUNT_MANAGEMENT_MEMBERSHIP_INVITE);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      aria-describedby="share-options-modal-description"
      aria-labelledby="share-options-modal-title"
      slotProps={{
        backdrop: {
          sx: {
            WebkitBackdropFilter: "blur(12px)",
            backdropFilter: "blur(12px)",
            backgroundColor: alpha(COLORS.PRIMARY_DARKEST, 0.52)
          }
        }
      }}
    >
      <Box
        data-testid="share-genomic-life-options-modal"
        sx={{
          left: "50%",
          maxWidth: "840px",
          position: "absolute",
          top: "50%",
          transform: "translate(-50%, -50%)",
          width: "calc(100% - 32px)"
        }}
      >
        <Box
          sx={{
            bgcolor: COLORS.WHITE,
            borderRadius: "16px",
            boxShadow: "0 24px 48px rgba(17, 24, 39, 0.18)",
            outline: 0,
            position: "relative",
            p: { sm: 4, xs: 3 },
            pt: { sm: 5, xs: 4 }
          }}
        >
          <IconButton
            aria-label="Close dialog"
            onClick={onClose}
            sx={{
              bgcolor: COLORS.WHITE,
              border: `1px solid ${COLORS.PRIMARY_LIGHT}`,
              boxShadow: "none",
              position: "absolute",
              right: { sm: -25, xs: 10 },
              top: { sm: -25, xs: 10 },
              "&:hover": {
                bgcolor: COLORS.LIGHT_GRAY,
                boxShadow: "none"
              }
            }}
          >
            <Box
              alt=""
              aria-hidden
              component="img"
              src={PlanInformationModalCloseIcon}
              sx={{ display: "block", height: 28, width: 28 }}
            />
          </IconButton>

          <Typography
            component="h4"
            id="share-options-modal-title"
            variant="h4_black"
            sx={{
              mb: 0.75
            }}
          >
            How would you like to share Genomic Life?
          </Typography>
          <Typography
            id="share-options-modal-description"
            variant="body"
            sx={{ mb: 3 }}
          >
            Please select an option below
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: { sm: "row", xs: "column" },
              gap: 3
            }}
          >
            <ButtonBase
              aria-label="Gift a membership. Buy a Genomic Life membership for your loved ones."
              focusRipple
              onClick={handleGiftMembershipOption}
              sx={shareModalOptionCardSx}
            >
              <Box
                alt=""
                aria-hidden
                component="img"
                src={PlanInformationGiftIcon}
                sx={{ display: "block", height: 79, width: 80 }}
              />
              <Typography
                component="h3"
                variant="body_strong_black"
                sx={{ mb: -1 }}
              >
                Gift a membership
              </Typography>
              <Typography variant="body_xs_black">
                Buy a Genomic Life membership for your loved one.
              </Typography>
            </ButtonBase>

            <ButtonBase
              aria-label="Tell someone about Genomic Life. Send someone a referral to sign up for Genomic Life."
              focusRipple
              onClick={handleTellSomeoneOption}
              sx={shareModalOptionCardSx}
            >
              <Box
                alt=""
                aria-hidden
                component="img"
                src={PlanInformationShareReferralIcon}
                sx={{ display: "block", height: 79, width: 80 }}
              />
              <Typography
                component="h3"
                variant="body_strong_black"
                sx={{ mb: -1 }}
              >
                Tell someone about Genomic Life
              </Typography>
              <Typography variant="body_xs_black">
                Send someone an invite so they can join.
              </Typography>
            </ButtonBase>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ShareGenomicLifeOptionsModal;
